import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardTitle, CardContent, CardDescription } from '@voilajsx/uikit/card';
import { Button } from '@voilajsx/uikit/button';
import { Alert, AlertTitle, AlertDescription } from '@voilajsx/uikit/alert';
import { ValidatedInput } from '@voilajsx/uikit/form';
import { 
  Upload, 
  Image, 
  AlertCircle,
  CheckCircle,
  Shield,
  Clock,
  Video,
  X
} from 'lucide-react';
import { SEO } from '../../../../shared/components';
import { config } from '../../../auth/config';

const UploadGalleryPage: React.FC = () => {
  const [files, setFiles] = useState<File[]>([]);
  const [fileTitles, setFileTitles] = useState<string[]>([]);
  const [fileDescriptions, setFileDescriptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{uploaded: number, total: number} | null>(null);
  const navigate = useNavigate();

  const getFileTypeIcon = (file: File) => {
    return file.type.startsWith('image/') ? Image : Video;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const selectedFiles = Array.from(e.target.files);
      
      // Validate each file
      const validFiles: File[] = [];
      const errors: string[] = [];
      
      selectedFiles.forEach((file, index) => {
        // File size validation (50MB limit)
        if (file.size > 50 * 1024 * 1024) {
          errors.push(`File ${index + 1} (${file.name}): Size must be less than 50MB`);
          return;
        }
        
        // File type validation
        const allowedTypes = [
          'image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/gif',
          'video/mp4', 'video/mpeg', 'video/quicktime', 'video/x-msvideo', 'video/webm'
        ];
        
        if (!allowedTypes.includes(file.type)) {
          errors.push(`File ${index + 1} (${file.name}): Only images (PNG, JPG, WebP, GIF) and videos (MP4, MPEG, MOV, AVI, WebM) are allowed`);
          return;
        }
        
        validFiles.push(file);
      });
      
      if (errors.length > 0) {
        setError(errors.join('\n'));
        return;
      }
      
      setFiles(validFiles);
      setFileTitles(validFiles.map(file => file.name.replace(/\.[^/.]+$/, ''))); // Remove extension
      setFileDescriptions(validFiles.map(() => ''));
      setError(null);
    }
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
    setFileTitles(prev => prev.filter((_, i) => i !== index));
    setFileDescriptions(prev => prev.filter((_, i) => i !== index));
  };

  const updateFileTitle = (index: number, title: string) => {
    setFileTitles(prev => prev.map((t, i) => i === index ? title : t));
  };

  const updateFileDescription = (index: number, description: string) => {
    setFileDescriptions(prev => prev.map((d, i) => i === index ? description : d));
  };

  const validate = () => {
    return files.length > 0 && fileTitles.every(title => title.trim() !== '');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (!validate()) {
      setError('Please select at least one file and provide titles for all files.');
      return;
    }

    setLoading(true);
    setUploadProgress({ uploaded: 0, total: files.length });

    const data = new FormData();
    
    // Add files
    files.forEach(file => {
      data.append('files', file);
    });
    
    // Add individual titles and descriptions
    fileTitles.forEach(title => {
      data.append('title[]', title);
    });
    
    fileDescriptions.forEach(description => {
      data.append('description[]', description);
    });

    try {
      const token = localStorage.getItem('auth_token');
      if (!token) throw new Error('User is not authenticated');

      console.log('📤 Starting gallery upload...', { fileCount: files.length });

      const res = await fetch(`${config.api.baseUrl}/api/gallery/upload`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: data
      });

      if (!res.ok) {
        const json = await res.json();
        throw new Error(json.error || `HTTP ${res.status}: ${res.statusText}`);
      }

      const result = await res.json();
      console.log('✅ Gallery items uploaded successfully:', result);

      setUploadProgress({ uploaded: result.uploaded, total: files.length });
      setSuccess(true);
      setTimeout(() => navigate('/gallery'), 2000);
    } catch (err: any) {
      console.error('❌ Upload failed:', err);
      setError(err.message || 'Failed to upload gallery items');
      setUploadProgress(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO
        title="Upload Gallery Items - Admin Panel"
        description="Upload new images and videos to the gallery"
      />
      <div className="container mx-auto px-4 max-w-6xl py-8">
        <div className="space-y-6">
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 via-violet-600 to-indigo-600 rounded-lg p-6 shadow-lg">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-lg backdrop-blur-sm">
                <Upload className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold tracking-tight text-white">
                  Upload Gallery Items
                </h1>
                <p className="mt-1 text-purple-100 text-sm">
                  Add images and videos to the public gallery for homepage display
                </p>
                <div className="mt-2 flex items-center gap-2 text-purple-100 text-xs">
                  <Shield className="h-3 w-3" />
                  <span>Files are stored securely with auto-renewing access links</span>
                </div>
              </div>
            </div>
          </div>

          {/* Upload Form Card */}
          <Card className="border-t-4 border-t-purple-600 shadow-md overflow-hidden p-0">
            <div className="bg-gradient-to-r from-purple-100 to-violet-100 px-6 py-5">
              <CardTitle className="flex items-center gap-2">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Image className="h-5 w-5 text-purple-600" />
                </div>
                Gallery Content
              </CardTitle>
              <CardDescription className="mt-1.5">
                Upload multiple images and videos with individual titles and descriptions
              </CardDescription>
            </div>
            
            <CardContent className="p-6">
              {error && (
                <Alert variant="destructive" className="mb-6">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Error</AlertTitle>
                  <AlertDescription className="whitespace-pre-line">{error}</AlertDescription>
                </Alert>
              )}
              
              {success && (
                <Alert className="mb-6 border-green-200 bg-green-50">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <AlertTitle className="text-green-800">Success</AlertTitle>
                  <AlertDescription className="text-green-700">
                    {uploadProgress && (
                      <>Gallery items uploaded successfully ({uploadProgress.uploaded}/{uploadProgress.total}). Redirecting...</>
                    )}
                  </AlertDescription>
                </Alert>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* File Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Files <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input 
                      type="file" 
                      multiple
                      accept="image/*,video/*"
                      onChange={handleFileChange} 
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 file:mr-4 file:py-1 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100"
                    />
                  </div>
                  <div className="mt-2 space-y-1">
                    <p className="text-xs text-gray-500">
                      Supported formats: Images (PNG, JPG, WebP, GIF) and Videos (MP4, MPEG, MOV, AVI, WebM)
                    </p>
                    <p className="text-xs text-gray-500">
                      Maximum 10 files, 50MB each
                    </p>
                    <div className="flex items-center gap-1 text-xs text-green-600">
                      <Shield className="h-3 w-3" />
                      <span>Files stored securely with automatic URL renewal</span>
                    </div>
                  </div>
                </div>

                {/* Selected Files */}
                {files.length > 0 && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Selected Files ({files.length})
                    </h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {files.map((file, index) => {
                        const FileIcon = getFileTypeIcon(file);
                        
                        return (
                          <Card key={index} className="border-l-4 border-l-purple-500">
                            <CardContent className="p-4">
                              <div className="flex items-start gap-3">
                                <div className="p-2 bg-purple-50 rounded-lg flex-shrink-0">
                                  <FileIcon className="h-5 w-5 text-purple-600" />
                                </div>
                                
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <p className="text-sm font-medium text-gray-900 truncate">
                                        {file.name}
                                      </p>
                                      <p className="text-xs text-gray-500">
                                        {file.type} • {(file.size / 1024 / 1024).toFixed(2)} MB
                                      </p>
                                    </div>
                                    
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="outline"
                                      onClick={() => removeFile(index)}
                                      className="ml-2 border-red-200 text-red-600 hover:bg-red-50"
                                    >
                                      <X className="h-3 w-3" />
                                    </Button>
                                  </div>
                                  
                                  <div className="mt-3 space-y-3">
                                    <ValidatedInput
                                      id={`title_${index}`}
                                      label="Title"
                                      required
                                      value={fileTitles[index] || ''}
                                      onChange={(value) => updateFileTitle(index, value)}
                                      placeholder="Enter title for this item"
                                    />
                                    
                                    <ValidatedInput
                                      id={`description_${index}`}
                                      label="Description (Optional)"
                                      value={fileDescriptions[index] || ''}
                                      onChange={(value) => updateFileDescription(index, value)}
                                      placeholder="Brief description"
                                    />
                                  </div>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Security Notice */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Clock className="h-5 w-5 text-amber-600 mt-0.5" />
                    <div>
                      <h4 className="text-sm font-medium text-amber-800">Gallery Security</h4>
                      <p className="text-sm text-amber-700 mt-1">
                        Images and videos are stored in secure cloud storage with signed URLs that auto-renew every 7 days. 
                        Items will be publicly visible on the homepage once uploaded.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex gap-4 pt-4">
                  <Button 
                    type="submit" 
                    disabled={loading || files.length === 0}
                    className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 disabled:opacity-50"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        {uploadProgress ? 
                          `Uploading... (${uploadProgress.uploaded}/${uploadProgress.total})` : 
                          'Uploading...'
                        }
                      </>
                    ) : (
                      <>
                        <Upload className="mr-2 h-4 w-4" />
                        Upload {files.length > 0 ? `${files.length} Items` : 'Gallery Items'}
                      </>
                    )}
                  </Button>
                  
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => navigate('/gallery')}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};

export default UploadGalleryPage;
